import { action, computed, observable } from 'mobx'
import { validationContext } from 'validx'
import { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { DocumentsQuestion } from '@/features/legends/questions/questions'

/**
 * Asks for documents. Deprecated: it can't be added or edited, but older
 * legends still have them, so it loads and saves as it is. It can still be
 * moved, removed and referenced by its ID.
 *
 * Existing ones are deliberately read-only, and options without an ID don't
 * get one generated: since the feature is deprecated, nothing about it
 * changes on save.
 */
export class DocumentsQuestionEditorViewModel extends QuestionEditorViewModelBase<DocumentsQuestion> {
  @observable.ref private accessor _question: DocumentsQuestion = {
    type: 'Documents',
  }

  readonly type = 'Documents'

  readonly validation = validationContext(this, {})

  @computed
  get question() {
    return this._question.question ?? ''
  }

  @computed
  get description() {
    return this._question.description ?? ''
  }

  /**
   * The documents it asks for.
   */
  @computed
  get options() {
    return this._question.options ?? []
  }

  /** @inheritDoc */
  get displayText() {
    return this.question
  }

  /**
   * Keeps the element as loaded. Its ID is loaded too, so that the elements
   * referencing it, and the IDs of the others, see it, and so are its
   * conditions, so that the card counts them. Nothing edits them.
   */
  @action
  hydrate(question: DocumentsQuestion) {
    this._question = question
    this._hydrateShared(question)
  }

  /**
   * The element as loaded, conditions included. Lists it leaves out are
   * saved empty, as every other element's are.
   */
  protected _serialize(): DocumentsQuestion {
    const { triggerWhen, options } = this._question
    return {
      ...this._question,
      id: this.id,
      triggerWhen: triggerWhen ?? [],
      options: (options ?? []).map((option) => ({
        ...option,
        triggerWhen: option.triggerWhen ?? [],
        documentTagIds: option.documentTagIds ?? [],
      })),
    }
  }
}
